import ElasticsearchAPI from '../api/ElasticsearchAPI';
import { formatDateWithUnicode } from '../util/time';

// a participant suggestion shown by name once picked: the default mapper
// names a survey response by its email
export const participantSuggestion = (hit: Record<string, any>) => ({
  ...ElasticsearchAPI.defaultSelectorMapFunction(hit),
  name: hit._source.name || hit._source.email,
});

export const ADDITIONAL_MANAGERS_FIELD = 'additional_managers_identifiers';

export type ManagerFieldValue = {
  email: string;
  name: string;
  external_id: string | number | null;
  platform_id: string | null;
};

// the manager field holds the person, shown by name; the identifiers are
// the record's own, so an untouched field writes back what was there
export const managerFieldValue = (
  configs: Record<string, any>,
  reverseKeyMapping: Record<string, string>
): ManagerFieldValue | '' => {
  const email = configs[reverseKeyMapping.manager_email];
  if (!email) {
    return '';
  }
  return {
    email,
    name: configs._manager_name || email,
    external_id: configs[reverseKeyMapping.manager_external_id] ?? null,
    platform_id: configs[reverseKeyMapping.manager_platform_id] ?? null,
  };
};

export type AdditionalManagerRow = {
  email: string | null;
  external_id: string | number | null;
  platform_id: string | null;
  name: string | null;
  // set when the row comes from a pick, not from typed text
  picked?: boolean;
};

// the rows of the additional managers table for the record's entries, the
// names resolved by the backend (an entry naming nobody shows its email)
export const additionalManagerRows = (
  entries: Record<string, any>[] | undefined,
  names: (string | null)[] | undefined
): AdditionalManagerRow[] =>
  (entries ?? []).map((entry, index) => ({
    email: entry.email ?? null,
    external_id: entry.external_id ?? null,
    platform_id: entry.platform_id ?? null,
    name: names?.[index] || entry.email || null,
  }));

// the row for a picked participant (an autosuggest selection) or for typed
// text, which names nobody by itself
export const additionalManagerRow = (value: any): AdditionalManagerRow => {
  if (value && typeof value === 'object') {
    const picked = value.object ?? value;
    return {
      email: picked.email ?? null,
      external_id: picked.external_id ?? null,
      platform_id: picked.platform_id ?? null,
      name: picked.name || picked.email || null,
      picked: true,
    };
  }
  return {
    email: null,
    external_id: null,
    platform_id: null,
    name: value || null,
    picked: false,
  };
};

const additionalManagerIdentifiers = (
  row: Record<string, any>
): Record<string, any> => ({
  email: row.email ?? null,
  external_id: row.external_id ?? null,
  platform_id: row.platform_id ?? null,
});

// the manager and every additional manager must come from a pick: typed
// text names a person by email alone and leaves the other identifiers
// stale. Rows that were not touched are fine.
export const validateDatasetRecordManagers = (
  modalData: Record<string, any>,
  fields: Record<string, any>,
  originalModalData: Record<string, any>,
  message: string
): Record<string, string> => {
  const errors: Record<string, string> = {};
  Object.entries(fields).forEach(([name, info]) => {
    const value = modalData[name];
    if (info.type === 'manager_email_editor') {
      if (
        typeof value === 'string' &&
        value.trim() &&
        value !== (originalModalData[name] ?? '')
      ) {
        errors[name] = message;
      }
    } else if (info.name === ADDITIONAL_MANAGERS_FIELD) {
      const originalEmails = (originalModalData[name] ?? []).map(
        (row) => row.email
      );
      if (
        (value ?? []).some(
          (row) => row.picked !== true && !originalEmails.includes(row.email)
        )
      ) {
        errors[name] = message;
      }
    }
  });
  return errors;
};

// translate from modal data to external (i.e. perf admin, survey response config) data
export const exportDatasetRecordFields = (
  modalData: Record<string, any>,
  fields: Record<string, any>,
  reverseKeyMapping: Record<string, string>
): Record<string, any> => {
  const data: Record<string, any> = {};
  // the columns following the manager picked are fields too: written
  // in field order, their previous value could overwrite the pick
  const managerData: Record<string, any> = {};
  Object.entries(fields).forEach(([name, info]) => {
    if (name in modalData) {
      if (info.type === 'date') {
        if (modalData[name] && typeof modalData[name] === 'object') {
          data[name] = formatDateWithUnicode(modalData[name], info.unicode);
        } else {
          data[name] = modalData[name];
        }
      } else if (info.type === 'choice') {
        if (modalData[name] && typeof modalData[name] === 'object') {
          data[name] = modalData[name].object;
        } else {
          data[name] = modalData[name];
        }
      } else if (info.type === 'boolean') {
        if (modalData[name] === undefined) {
          modalData[name] = info.defaultValue;
        }

        data[name] = modalData[name] ? info.trueChoice : info.falseChoice;
      } else if (info.type === 'manager_email_editor') {
        if (modalData[name] && typeof modalData[name] === 'object') {
          // a suggestion picked ({ object }) or the record's own manager
          const manager = modalData[name].object ?? modalData[name];
          managerData[reverseKeyMapping.manager_email] = manager.email;
          if ('manager_full_name' in reverseKeyMapping) {
            managerData[reverseKeyMapping.manager_full_name] = manager.name;
          }
          if ('manager_external_id' in reverseKeyMapping) {
            managerData[reverseKeyMapping.manager_external_id] =
              manager.external_id ?? null;
          }
          if ('manager_platform_id' in reverseKeyMapping) {
            // the sync matches managers on platform id first: a stale
            // one would win over the manager just picked
            managerData[reverseKeyMapping.manager_platform_id] =
              manager.platform_id ?? null;
          }
        } else {
          data[name] = modalData[name];
        }
      } else if (info.name === ADDITIONAL_MANAGERS_FIELD) {
        data[name] = (modalData[name] ?? []).map(additionalManagerIdentifiers);
      } else {
        data[name] = modalData[name];
      }
    } else {
      data[name] = undefined;
    }
  });

  return { ...data, ...managerData };
};
